import { useEffect } from "react";
import { memoryStorageDriver } from "src/storage/memoryStorage.driver.ts";
import { normalizePathname } from "src/lib/tabs/normalizePathname.ts";
import { replacePathParams } from "@exonn/utils";
import type { ModuleTabsStore } from "src/framework/layout/ModuleTabsProvider.tsx";
import { useResourceStorageKeys } from "src/framework/useResourceStorageKeys.tsx";
import type { FormTabsConfig } from "src/system/module.types.ts";

export const useSubscribeToUnsavedChanges = ({
  moduleTabsStore,
  resource,
  formTabsConfig,
  idParam = "id",
  createRequestParams,
}: {
  moduleTabsStore: ModuleTabsStore;
  resource: string;
  formTabsConfig: FormTabsConfig<any, any> | undefined;
  idParam?: string;
  createRequestParams?: any;
}) => {
  const storageKeys = useResourceStorageKeys({
    resource: resource,
  });

  useEffect(() => {
    const cleanFns = moduleTabsStore.paths.flatMap((path) => {
      const editPathIndex = moduleTabsStore.editPaths.findIndex(
        (p) => path === p,
      );
      const createPathIndex = moduleTabsStore.createPaths.findIndex(
        (p) => path === p,
      );
      if (createPathIndex != -1) {
        const storageKey = storageKeys.form({
          type: "create",
          requestParams: createRequestParams,
        });

        const unsub = memoryStorageDriver.on("change", storageKey, (value) => {
          const hasUnsavedChanges = !!value;
          const tabId = normalizePathname(
            replacePathParams(
              moduleTabsStore.createPath!,
              moduleTabsStore.createParams[createPathIndex] ?? {},
            ),
          );
          const tab = moduleTabsStore.baseStore.tabs.find(
            (tab) => tab.id === tabId,
          );
          if (tab) {
            tab.setHasUnsavedChanges(hasUnsavedChanges);
          }
        });
        return [unsub];
      }
      if (editPathIndex != -1) {
        const currentId = moduleTabsStore.editParams[editPathIndex][idParam];

        const watchedStorageKeys = Object.keys(
          formTabsConfig || {
            general: {},
          },
        ).map((key) =>
          storageKeys.form({
            id: currentId,
            type: "edit",
            tab: key,
          }),
        );

        return watchedStorageKeys.map((storageKey) => {
          return memoryStorageDriver.on("change", storageKey, (value) => {
            const hasUnsavedChanges = watchedStorageKeys.some((key) => {
              return !!memoryStorageDriver.get(key);
            });
            const tabId = normalizePathname(
              replacePathParams(
                moduleTabsStore.editPath!,
                moduleTabsStore.editParams[editPathIndex],
              ),
            );
            const tab = moduleTabsStore.baseStore.tabs.find(
              (tab) => tab.id === tabId,
            );
            if (tab) {
              tab.setHasUnsavedChanges(hasUnsavedChanges);
            }
          });
        });
      }

      return [];
    });

    return () => {
      cleanFns.forEach((cleanFn) => cleanFn());
    };
  }, [
    moduleTabsStore.paths,
    moduleTabsStore.editPaths,
    moduleTabsStore.editParams,
    moduleTabsStore.createParams,
    moduleTabsStore.createPath,
    moduleTabsStore.editPath,
    moduleTabsStore.createPaths,
    moduleTabsStore.baseStore.tabs,
    storageKeys,
    formTabsConfig,
    createRequestParams,
    idParam,
  ]);
};
